export const PUBLIC_ACCOUNTS_API_ENDPOINTS = {
  LIST: "/internal/operations/public-accounts",
  getById: (id: string) => `/internal/operations/public-accounts/${id}`,
  copy: (id: string) => `/internal/operations/public-accounts/${id}/copy`,
  getCopyStats: (id: string) => `/internal/operations/public-accounts/${id}/copy-stats`,
} as const;

// Atoms only. `/auth/me` projects the RBAC-effective permissions and that projection emits
// `read`/`write`/`delete` exclusively, so the former `:update` / `:admin` entries could never match.
// Dropping them changes nothing at runtime: the backend expands `update` → `write` and
// `admin` → read+write+delete, so a holder of either already carries the atom listed here.
export const PUBLIC_ACCOUNTS_READ_SCOPES = [
  "operations:public_accounts:read",
  "operations:public_accounts:write",
  "operations:public_accounts:delete",
] as const;

export const PUBLIC_ACCOUNTS_ADD_SCOPES = [
  "operations:public_accounts:write",
  "operations:public_accounts:delete",
] as const;

export const PUBLIC_ACCOUNTS_EDIT_SCOPES = ["operations:public_accounts:write"] as const;

export const PUBLIC_ACCOUNTS_DELETE_SCOPES = ["operations:public_accounts:delete"] as const;

/** Códigos de "país" en el formulario de cuentas públicas que representan stablecoin (crypto wallet). */
export const CRYPTO_WALLET_PUBLIC_COUNTRY_CODES = ["USDT", "USDC"] as const;

export function isCryptoWalletPublicCountryCode(code: string): boolean {
  return (CRYPTO_WALLET_PUBLIC_COUNTRY_CODES as readonly string[]).includes(code.trim());
}

const PUBLIC_ACCOUNT_COUNTRY_OPTIONS_RAW: { value: string; label: string }[] = [
  { value: "USA", label: "United States" },
  { value: "COL", label: "Colombia" },
  { value: "BRA", label: "Brazil" },
  { value: "MEX", label: "México" },
  { value: "ARG", label: "Argentina" },
  { value: "CHL", label: "Chile" },
  { value: "PER", label: "Perú" },
  { value: "PRY", label: "Paraguay" },
  { value: "URY", label: "Uruguay" },
  { value: "ECU", label: "Ecuador" },
  { value: "PAN", label: "Panamá" },
  { value: "CHN", label: "China" },
  { value: "HKG", label: "Hong Kong" },
  { value: "CRI", label: "Costa Rica" },
  { value: "GTM", label: "Guatemala" },
  { value: "USDT", label: "USDT" },
  { value: "USDC", label: "USDC - Activo digital" },
];

export const PUBLIC_ACCOUNT_COUNTRY_OPTIONS = [...PUBLIC_ACCOUNT_COUNTRY_OPTIONS_RAW].sort((a, b) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
);

export const COUNTRY_CODE_TO_ISO2: Record<string, string> = {
  USA: "US",
  COL: "CO",
  BRA: "BR",
  MEX: "MX",
  ARG: "AR",
  CHL: "CL",
  PER: "PE",
  PRY: "PY",
  URY: "UY",
  ECU: "EC",
  PAN: "PA",
  CHN: "CN",
  HKG: "HK",
  CRI: "CR",
  GTM: "GT",
  USDT: "",
  USDC: "",
};

export const COUNTRY_CODE_TO_CURRENCY: Record<string, string> = {
  USA: "USD",
  COL: "COP",
  BRA: "BRL",
  MEX: "MXN",
  ARG: "ARS",
  CHL: "CLP",
  PER: "PEN",
  PRY: "PYG",
  URY: "UYU",
  ECU: "USD",
  PAN: "USD",
  CHN: "CNY",
  HKG: "HKD",
  CRI: "CRC",
  GTM: "GTQ",
  USDT: "USDT",
  USDC: "USDC",
};

/** Crypto wallet network options (aligned con stablecoins en payout-requests / cuentas). */
export const CRYPTO_NETWORK_OPTIONS: { value: string; label: string }[] = [
  { label: "TRC20", value: "TRC20" },
  { label: "BEP20", value: "BEP20" },
  { label: "Polygon", value: "Polygon" },
  { label: "Solana", value: "Solana" },
  { label: "ERC20", value: "ERC20" },
];

export const COUNTRY_CODE_COLOMBIA = "COL";
export const BANK_NAME_ACH = "ACH";

export function shouldShowAccountTypeAndNumberFields(
  countryCode: string | undefined,
  bankName: string | undefined,
): boolean {
  return countryCode === COUNTRY_CODE_COLOMBIA && bankName === BANK_NAME_ACH;
}

export const PUBLIC_ACCOUNTS_PAGE_SIZE = 24;

export const TRACKING_PAGE_SIZE = 25;

export const TRACKING_PAGINATION_SIZES = [10, 25, 50, 100] as const;

export const SEARCH_DEBOUNCE_MS = 400;

export const SEARCH_OVERLAY_DURATION_MS = 350;
